import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { ssrDocument } from '@/utils/ssrUtils';

interface Heading {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  content: string;
  readingTime: string;
  className?: string;
}

/**
 * Table of Contents component for blog posts
 * - Automatically extracts headings from content
 * - Provides smooth scrolling navigation
 * - Highlights current section
 * - Improves content structure for SEO
 */
export const TableOfContents: React.FC<TableOfContentsProps> = ({
  content,
  readingTime,
  className
}) => {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [showAllHeadings, setShowAllHeadings] = useState(false);

  // Extract headings from content and add IDs to actual DOM elements
  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    
    const extractHeadings = (htmlContent: string): Heading[] => {
      // SSR-safe heading extraction
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        // Fallback for SSR - use regex to extract headings
        const headingRegex = /<h([12])[^>]*>(.*?)<\/h[12]>/gi;
        const extractedHeadings: Heading[] = [];
        let match;
        let index = 0;
        
        while ((match = headingRegex.exec(htmlContent)) !== null) {
          const level = parseInt(match[1]);
          const text = match[2].replace(/<[^>]*>/g, '').trim();
          const id = text
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '') + (index > 0 ? `-${index}` : '');
          
          extractedHeadings.push({ id, text, level });
          index++;
        }
        
        return extractedHeadings;
      }
      
      const extractedHeadings: Heading[] = [];
      const tempDiv = ssrDocument.createElement('div');
      if (!tempDiv) return extractedHeadings;
      tempDiv.innerHTML = htmlContent;
      
      // Only select main headers (h1 and h2), exclude sub-headers
      const headingElements = tempDiv.querySelectorAll('h1, h2');

      headingElements.forEach((heading, index) => {
        const level = parseInt(heading.tagName.charAt(1));
        const text = heading.textContent || '';
        let id = heading.id;
        
        // Generate ID if not present
        if (!id) {
          id = text
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
          
          // Ensure uniqueness
          if (extractedHeadings.some(h => h.id === id)) {
            id = `${id}-${index}`;
          }
        }

        extractedHeadings.push({ id, text, level });
      });

      return extractedHeadings;
    };

    const extracted = extractHeadings(content);
    setHeadings(extracted);

    // Add IDs to actual DOM headings after content is rendered
    setTimeout(() => {
      const actualHeadings = ssrDocument.querySelectorAll('article h1, article h2');
      actualHeadings.forEach((heading, index) => {
        if (extracted[index] && !heading.id) {
          heading.id = extracted[index].id;
        }
      });
    }, 100);
  }, [content]);

  // Track active section
  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined' || !window.IntersectionObserver) return;
    
    const observerOptions = {
      rootMargin: '-100px 0px -66% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      // Find the heading that's closest to the top of the viewport
      let closestToTop = null;
      let minDistance = Infinity;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const rect = entry.boundingClientRect;
          const distanceFromTop = Math.abs(rect.top);
          
          if (distanceFromTop < minDistance) {
            minDistance = distanceFromTop;
            closestToTop = entry.target.id;
          }
        }
      });

      if (closestToTop) {
        setActiveId(closestToTop);
      }
    }, observerOptions);

    // Observe all headings
    headings.forEach(({ id }) => {
      const element = ssrDocument.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [headings]);

  // Smooth scroll to heading
  const scrollToHeading = (id: string) => {
    // Only run in browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    
    const element = ssrDocument.getElementById(id) as HTMLElement;
    if (element) {
      const elementPosition = element.offsetTop;
      const offsetPosition = elementPosition - 80; // Account for header
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  if (headings.length === 0) {
    return null;
  }

  const displayedHeadings = showAllHeadings ? headings : headings.slice(0, 8);
  const remainingCount = headings.length - 8;

  return (
    <nav 
      className={cn(
        "bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-lg p-3",
        "sticky top-8", // Make it sticky
        className
      )}
      aria-label="Table of contents"
    >
      <h2 className="text-xs font-medium text-gray-400 mb-3 uppercase tracking-wide">
        Contents
      </h2>
      
      {/* Expandable container that grows taller */}
      <div className="transition-all duration-300 ease-in-out">
        <ol className="space-y-1 text-xs">
          {displayedHeadings.map(({ id, text, level }) => (
            <li key={id}>
              <button
                onClick={() => scrollToHeading(id)}
                className={cn(
                  "block w-full text-left py-1.5 px-2 rounded transition-all duration-200",
                  "hover:bg-gray-800/50 hover:text-gray-200",
                  "focus:outline-none focus:ring-1 focus:ring-coderide-light-blue/50",
                  activeId === id 
                    ? "bg-coderide-light-blue/10 text-coderide-light-blue font-medium border-l-2 border-coderide-light-blue" 
                    : "text-gray-400 hover:text-gray-300",
                  // Indent based on heading level
                  level === 2 && "ml-0",
                  level === 3 && "ml-2",
                  level === 4 && "ml-4",
                  level === 5 && "ml-6",
                  level === 6 && "ml-8"
                )}
              >
                <span className="line-clamp-2 text-xs leading-relaxed">{text}</span>
              </button>
            </li>
          ))}
          
          {/* Expandable section for remaining headings */}
          {remainingCount > 0 && (
            <li>
              <button
                onClick={() => setShowAllHeadings(!showAllHeadings)}
                className="block w-full text-left py-1.5 px-2 rounded transition-all duration-200 hover:bg-gray-800/50 text-gray-500 hover:text-gray-300 italic"
              >
                {showAllHeadings ? (
                  <span className="flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                    Show less
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    +{remainingCount} more sections...
                  </span>
                )}
              </button>
            </li>
          )}
        </ol>
      </div>
      
      {/* Reading progress indicator */}
      <div className="mt-3 pt-3 border-t border-gray-800">
        <ReadingProgress readingTime={readingTime} />
      </div>
    </nav>
  );
};

/**
 * Reading progress component with enhanced details
 */
interface ReadingProgressProps {
  readingTime: string;
}

const ReadingProgress: React.FC<ReadingProgressProps> = ({ readingTime }) => {
  const [progress, setProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    
    const updateProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = ssrDocument.documentElement?.scrollHeight ? ssrDocument.documentElement.scrollHeight - window.innerHeight : 0;
      const scrollPercent = (scrollTop / docHeight) * 100;
      const normalizedProgress = Math.min(100, Math.max(0, scrollPercent));
      
      setProgress(normalizedProgress);
      setScrollPosition(scrollTop);
      
      // Extract minutes from readingTime string (e.g., "5 min read" -> 5)
      const remainingPercent = 100 - normalizedProgress;
      const estimatedReadingTime = parseInt(readingTime.match(/\d+/)?.[0] || '5');
      const remaining = (remainingPercent / 100) * estimatedReadingTime;
      setTimeRemaining(remaining);
    };

    window.addEventListener('scroll', updateProgress);
    updateProgress(); // Initial calculation

    return () => window.removeEventListener('scroll', updateProgress);
  }, [readingTime]);

  const formatTimeRemaining = (minutes: number) => {
    if (minutes < 1) return "< 1 min";
    return `${Math.ceil(minutes)} min`;
  };

  return (
    <div className="space-y-2">
      {/* Progress bar with gradient */}
      <div className="flex items-center space-x-2">
        <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-coderide-light-blue to-coderide-purple transition-all duration-150 ease-out rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-xs font-medium text-gray-300 min-w-[2.5rem]">
          {Math.round(progress)}%
        </span>
      </div>
      
      {/* Additional details */}
      <div className="flex justify-center items-center text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {formatTimeRemaining(timeRemaining)} left
        </span>
      </div>
    </div>
  );
};

export default TableOfContents;
